import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, takeUntil } from 'rxjs';

import { Memo } from 'shared/models';
import { APIError, MemosAPIService, SharedUserService } from 'shared/services';
import { DialogService, ToastService } from 'shared/ui';

import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { EventBusService } from '../../services/event-bus.service';
import { UIStateService } from '../../services/ui-state.service';

import { AnnotationListComponent } from '../annotation-list/annotation-list.component';
import { MemoExportDialogComponent } from '../../dialogs/memo-export-dialog/memo-export-dialog.component';
import { MemoDialogComponent } from '../../dialogs/memo-dialog/memo-dialog.component';
import { MemoSharingDialogComponent } from '../../dialogs/memo-sharing-dialog/memo-sharing-dialog.component';

function createCopyText(memo: Memo): string {
  return `${memo.text}\n:${memo.content}`;
}

function createMultipleCopyText(memos: Memo[]): string {
  return memos.map((memo) => '- ' + createCopyText(memo)).join('\n\n');
}

@Component({
  selector: 'viewer-memo-list',
  templateUrl: './memo-list.component.html',
  styleUrls: [
    '../annotation-list/annotation-list.component.scss',
    './memo-list.component.scss',
  ],
})
export class MemoListComponent
  extends AnnotationListComponent<Memo>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('listContainer', { static: false })
  listContainer?: ElementRef;

  private _unsubscriber = new Subject<void>();
  private _scrollTimeout?: any; // 스크롤 타이머 추가

  public _groupId?: number;
  private _previousGroupId?: number; // 이전 그룹 ID 추적
  public _isSingleMode = false;

  public _isCommentaryEditorMode = false;
  public _focusedMemoId?: string; // 포커스된 메모 ID 추적

  constructor(
    private _clipboard: Clipboard,
    private _memosAPIService: MemosAPIService,
    private _toastService: ToastService,
    private _dialogService: DialogService,
    private _annotationsService: AnnotationsV2Service,
    private _eventBusService: EventBusService,
    private _uiStateService: UIStateService,
    private _userService: SharedUserService
  ) {
    super();
  }

  @Input() bid?: string;

  override ngOnInit(): void {
    this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;

    super.ngOnInit();

    this._eventBusService
      .on('ContentsComponent:memoUpdated')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this.loadItems();
      });

    this._annotationsService.state$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((state) => {
        // 그룹이 변경되었는지 확인
        if (
          this._previousGroupId !== undefined &&
          this._previousGroupId !== state.groupId
        ) {
          // 그룹 변경 시 본문보기 해제
          this._clearFocusedMemo();
        }
        this._previousGroupId = state.groupId;
        this._groupId = state.groupId;
      });

    this._eventBusService
      .on('MemoListComponent:memoDeleted')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        this._onItemsDeleted([data.memoId]);
      });

    // 메모로 스크롤 이동 이벤트 구독
    this._eventBusService
      .on('CommentRightPanelComponent:scrollToMemo')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ memoId, source }) => {
        if (source === 'memo-list') {
          setTimeout(() => {
            this._scrollToMemo(memoId);
          }, 300);
        }
      });

    // 본문 드래그 시 focus된 메모 본문 보기 해제 처리
    this._eventBusService
      .on('ContentsComponent:select')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        // focus된 메모 배경색 제거
        const existingFocusedItems = document.querySelectorAll(
          '.list-item.focused-memo'
        );
        existingFocusedItems.forEach((focusedItem) => {
          focusedItem.classList.remove('focused-memo');
        });

        // focus된 메모 ID 초기화
        this._focusedMemoId = undefined;
      });
  }

  ngAfterViewInit(): void {
    if (this.listContainer) {
      this.listContainer.nativeElement.addEventListener('scroll', () => {
        this._closeAllMenus();

        // 스크롤 중일 때 클래스 추가
        this.listContainer!.nativeElement.classList.add('is-scrolling');

        // 기존 타이머가 있으면 제거
        if (this._scrollTimeout) {
          clearTimeout(this._scrollTimeout);
        }

        // 스크롤이 멈춘 후 300ms 후에 클래스 제거
        this._scrollTimeout = setTimeout(() => {
          this.listContainer!.nativeElement.classList.remove('is-scrolling');
        }, 300);
      });
    }
  }

  ngOnDestroy(): void {
    // 타이머 정리
    if (this._scrollTimeout) {
      clearTimeout(this._scrollTimeout);
    }

    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  public override loadItems(): void {
    if (!this.bid) {
      return;
    }

    this._memosAPIService
      .get(
        this.bid,
        0,
        999,
        this._isCommentaryEditorMode
          ? {
              commentary_id: this._annotationsService.getState().commentaryId,
              filter: 'theirs',
            }
          : {
              group_id: this._groupId,
              filter: 'mine',
            }
      )
      .subscribe((response) => {
        this._isSingleMode = this._groupId == null;

        this._items = response.items.map((memo) => ({
          ...memo,
          selected: false,
          updatedDateTime: new Date(memo.created_at).getTime(),
        }));
        this._sortItems();
      });
  }

  _onItemClick(item: Memo): void {
    // 기존 다른 list-item에 있는 focused-memo 클래스 모두 제거
    const existingFocusedItems = document.querySelectorAll(
      '.list-item.focused-memo'
    );
    existingFocusedItems.forEach((focusedItem) => {
      focusedItem.classList.remove('focused-memo');
    });

    // 현재 클릭한 메모 요소 찾기
    const memoElement = document.querySelector(
      `.list-item[data-memo-id="${item.id}"]`
    ) as HTMLElement | null;

    // focused-memo 클래스가 이미 있으면 제거, 없으면 추가 (토글)
    if (memoElement) {
      if (this._focusedMemoId === item.id) {
        memoElement.classList.remove('focused-memo');
        this._focusedMemoId = undefined;

        this._eventBusService.fire('AnnotationsComponent:clearHighlight');
      } else {
        memoElement.classList.add('focused-memo');
        this.itemClicked.emit(item.url);
        this._focusedMemoId = item.id;
      }
    }
  }

  _onCopySelectedItemsButtonClick(): void {
    this._clipboard.copy(createMultipleCopyText(this._getSelectedItems()));
    this._toastService.open('메모가 복사되었습니다.');
  }

  _onDeleteSelectedItemsButtonClick(): void {
    const ids = this._getSelectedItems().map((item) => item.id);
    this._deleteItems(ids);
  }

  _onEditMenuClick(memo: Memo): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._dialogService
      .open(MemoDialogComponent, {
        data: {
          data: {
            id: memo.id,
            text: memo.text,
            content: memo.content,
            pinned: memo.pinned_at != null,
          },
          type: 'memoEdit',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.deleted) {
          this._onItemsDeleted([memo.id]);
        } else if (result?.updated) {
          memo.content = result.updated.content;
          memo.updated_at = new Date().toISOString();
        }
      });
  }

  _onDeleteMenuClick(memo: Memo): void {
    this._deleteItems([memo.id]);
  }

  _onExportMenuClick(memo: Memo): void {
    this._dialogService.open(MemoExportDialogComponent, {
      data: {
        id: memo.id,
        content: memo.content,
      },
    });
  }

  _onCopyMenuClick(memo: Memo): void {
    this._clipboard.copy(createCopyText(memo));
    this._toastService.open('메모가 복사되었습니다.');
  }

  _onShareButtonClick(memo: Memo): void {
    const user = this._userService.getUser();

    if (!user) {
      return;
    }

    this._dialogService.open(MemoSharingDialogComponent, {
      data: {
        memo,
        author: {
          nickname: user.nickname,
          avatar_image_url: user.avatar_image_url,
        },
      },
    });
  }

  private _deleteItems(ids: string[]): void {
    if (
      this._uiStateService.isCommentaryEditor &&
      !this._uiStateService.canEditCommentary
    ) {
      this._toastService.openWarning(
        '판매 설정이 완료된 코멘터리는 수정이 불가합니다.'
      );

      return;
    }

    this._annotationsService.deleteMemosById(ids).subscribe({
      next: () => {
        this._onItemsDeleted(ids);
        this._toastService.open('메모가 삭제되었습니다.');
      },
      error: (error: APIError) => {
        this._toastService.open(error.message);
      },
    });
  }

  private _onItemsDeleted(ids: string[]): void {
    this._items = this._items.filter((item) => ids.indexOf(item.id) === -1);
    this._onItemSelectionChange();
  }

  _onClickMemoCommentButton(memo: Memo): void {
    // 이벤트를 통해 comment-right-panel 열기 요청
    this._eventBusService.fire('MemoListComponent:commentButtonClick', {
      memo: memo,
      source: 'memo-list', // 소스 정보 추가
    });
  }

  // 특정 메모로 스크롤 이동하는 메서드 추가
  private _scrollToMemo(memoId: string): void {
    // 기존 다른 list-item에 있는 focused-memo 클래스 모두 제거
    const existingFocusedItems = document.querySelectorAll(
      '.list-item.focused-memo'
    );
    existingFocusedItems.forEach((item) => {
      item.classList.remove('focused-memo');
    });

    // 포커스된 메모 ID 업데이트
    this._focusedMemoId = memoId;

    // 해당 메모 요소 찾기 (list-item)
    const memoElement = document.querySelector(
      `.list-item[data-memo-id="${memoId}"]`
    ) as HTMLElement | null;

    if (memoElement && this.listContainer) {
      // 스크롤 가능한 부모 컨테이너 찾기 (.body 클래스)
      const scrollContainer = this.listContainer?.nativeElement;

      setTimeout(() => {
        if (scrollContainer) {
          // 컨테이너와 메모 요소의 위치 계산
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = memoElement.getBoundingClientRect();

          // 컨테이너의 현재 스크롤 위치
          const currentScrollTop = scrollContainer.scrollTop;

          // 메모를 컨테이너 중앙에 위치시키기 위한 스크롤 계산
          const elementOffsetFromContainer =
            elementRect.top - containerRect.top;
          const targetScrollTop = currentScrollTop + elementOffsetFromContainer;

          // 부드러운 스크롤로 이동
          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          });
        } else {
          // 컨테이너를 찾지 못한 경우 기본 동작
          memoElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 300);

      // 포커스 효과를 위한 클래스 추가
      memoElement?.classList.add('focused-memo');
    }
  }

  // 모든 열린 메뉴를 닫는 메서드
  private _closeAllMenus(): void {
    const backdropClickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: 0,
      clientY: 0,
    });

    // document.body에 클릭 이벤트를 발생시켜 CDK의 backdrop 클릭 처리 로직을 트리거
    document.body.dispatchEvent(backdropClickEvent);

    // 또는 더 직접적으로 overlay backdrop을 찾아서 클릭 이벤트 발생
    const overlayBackdrop = document.querySelector('.cdk-overlay-backdrop');
    if (overlayBackdrop) {
      overlayBackdrop.dispatchEvent(backdropClickEvent);
    }
  }

  // 본문보기 상태를 해제하는 메서드
  private _clearFocusedMemo(): void {
    // 기존 다른 list-item에 있는 focused-memo 클래스 모두 제거
    const existingFocusedItems = document.querySelectorAll(
      '.list-item.focused-memo'
    );
    existingFocusedItems.forEach((focusedItem) => {
      focusedItem.classList.remove('focused-memo');
    });

    // 포커스된 메모 ID 초기화
    this._focusedMemoId = undefined;

    // 하이라이트 제거 이벤트 발생
    this._eventBusService.fire('AnnotationsComponent:clearHighlight');
  }
}
